import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { TextArea } from "@/components/Textarea";
import { Label } from "@/components/Label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useCreateOrUpdateMenuItemMutation,
  useGetMenuItemOptionsQuery,
  useUpdateProductMutation,
} from "@/redux/services/menu/menu.api";
import toast from "react-hot-toast";
import {
  ItemOption,
  ItemOptionPrice,
  MenuItem,
  MenuItemOption,
  MenuItemOptionValue,
  Section,
  ValueDependentPrice,
} from "@/redux/services/menu/menu.type";
import { useUploadMedia } from "@/lib/useUploadMedia";
import { optionPriceToCent, pickPlacementPriceRows } from "@/lib/optionPrices";
import {
  cn,
  convertPriceFromCent,
  convertPriceToCent,
} from "@/lib/utils";
import Select, { MultiValue } from "react-select";
import { logger } from "@/services/Logger";
import { ChevronDown } from "lucide-react";
import { toastApiError } from "@/lib/apiErrors";

interface MenuItemFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  menuItem?: MenuItem;
  section?: Section;
  onSuccess?: () => void;
}

interface SelectedOption {
  label: string;
  value: string;
  isTemplate?: boolean;
  dependsOnOption?: MenuItemOption | null;
  menuItemOptionValues?: MenuItemOptionValue[];
  raw?: MenuItemOption;
}

interface PriceModifier {
  "@id"?: string;
  priceModifier: number;
  menuItemOptionValue: string;
}

type PriceModifiersMap = {
  [optionId: string]: { [valueId: string]: number };
};

interface ItemOptionWithPrices {
  menuItemOption: string;
  "@id"?: string;
  itemOptionPrices?: PriceModifier[];
  isRequired?: boolean;
  maxSelections?: number;
}

interface MenuItemFormData {
  title: string;
  description: string;
  basePrice: number;
  sortOrder: number;
  menuSection: string | undefined;
  image?: string;
  isAvailable: boolean;
  itemOptions: ItemOptionWithPrices[];
  "@id"?: string;
}

const Field = ({ children }: { children: React.ReactNode }) => (
  <div className="[&>label]:block [&>label]:mb-1 [&>label]:text-sm [&>label]:text-card-foreground">
    {children}
  </div>
);

const Section_ = ({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section>
        <CollapsibleTrigger className="w-full flex items-center justify-between py-2 border-b border-border text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-card-foreground transition-colors">
          <span>{title}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              open && "rotate-180"
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 space-y-4">
          {children}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
};

const getMinSelections = (type?: string): number => {
  switch (type) {
    case "radio":
    case "select":
      return 1;
    case "checkbox":
      return 0;
    default:
      return 1;
  }
};

const getMaxSelections = (
  type?: string,
  optionValuesLength?: number
): number => {
  switch (type) {
    case "radio":
      return 1;
    case "select":
    case "checkbox":
      return optionValuesLength ?? 1;
    default:
      return optionValuesLength ?? 1;
  }
};

const getDefaultMaxSelections = (
  type?: string,
  optionValuesLength?: number
): number => {
  switch (type) {
    case "radio":
    case "select":
      return 1;
    case "checkbox":
      return optionValuesLength ?? 1;
    default:
      return 1;
  }
};

const MenuItemFormDrawer: React.FC<MenuItemFormDrawerProps> = ({
  isOpen,
  onClose,
  menuItem,
  section,
  onSuccess,
}) => {
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [basePrice, setBasePrice] = useState<number>(0);
  // True when this menu already carries its own price for the dish, i.e. the
  // dish is on several menus and this one diverges.
  const [hasMenuSpecificPrice, setHasMenuSpecificPrice] =
    useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<number>(1);
  const [isAvailable, setIsAvailable] = useState<boolean>(true);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<SelectedOption[]>([]);
  const [optionPriceModifiers, setOptionPriceModifiers] =
    useState<PriceModifiersMap>({});
  const [optionSettings, setOptionSettings] = useState<{
    [optionId: string]: { isRequired: boolean; maxSelections: number };
  }>({});

  const [saveMenuItem, { isLoading }] = useCreateOrUpdateMenuItemMutation();
  const [updateProduct] = useUpdateProductMutation();
  const { uploadMedia } = useUploadMedia();
  const { data: menuItemOptionsData } = useGetMenuItemOptionsQuery({});

  const isEditing = !!menuItem;

  const initFormDataFromMenuItem = (item: MenuItem): void => {
    setTitle(item.title);
    setDescription(item.description || "");
    // `price` is what this menu actually charges; `basePrice` is the legacy
    // write column and goes stale as soon as a dish is shared across menus.
    setBasePrice(convertPriceFromCent(item.price ?? item.basePrice));
    setHasMenuSpecificPrice(
      item.priceOverride !== null && item.priceOverride !== undefined
    );
    setSortOrder(Number(item.sortOrder) || 1);
    setIsAvailable(item.isAvailable);
    setImagePreview(item.image?.url ?? "");
    initOptionsAndPriceModifiers(item);
  };

  const initOptionsAndPriceModifiers = (item: MenuItem): void => {
    if (!item.itemOptions || item.itemOptions.length === 0) {
      setSelectedOptions([]);
      setOptionPriceModifiers({});
      setOptionSettings({});
      return;
    }

    const options: SelectedOption[] = [];
    const priceModifiers: PriceModifiersMap = {};
    const settings: {
      [optionId: string]: { isRequired: boolean; maxSelections: number };
    } = {};

    item.itemOptions.forEach((option: ItemOption) => {
      options.push({
        value: option.menuItemOption.id,
        label: option.menuItemOption.title,
        isTemplate: option.menuItemOption.isTemplate,
        dependsOnOption: option.menuItemOption.dependsOnOption,
        menuItemOptionValues: option.menuItemOption.menuItemOptionValues,
        raw: option.menuItemOption,
      });

      const placementRows = pickPlacementPriceRows(
        option.itemOptionPrices,
        item.id
      );
      if (Object.keys(placementRows).length > 0) {
        const valuePrices: { [valueId: string]: number } = {};
        Object.entries(placementRows).forEach(
          ([valueId, price]: [string, ItemOptionPrice]) => {
            valuePrices[valueId] = convertPriceFromCent(price.priceModifier);
          }
        );
        priceModifiers[option.menuItemOption.id] = valuePrices;
      }

      settings[option.menuItemOption.id] = {
        isRequired: option.isRequired,
        maxSelections: option.maxSelections,
      };
    });

    setSelectedOptions(options);
    setOptionPriceModifiers(priceModifiers);
    setOptionSettings(settings);
  };

  const resetFormData = (): void => {
    setTitle("");
    setDescription("");
    setBasePrice(0);
    setSortOrder((section?.menuItems?.length ?? 0) + 1);
    setImagePreview("");
    setImageFile(null);
    setSelectedOptions([]);
    setOptionPriceModifiers({});
    setOptionSettings({});
  };

  useEffect(() => {
    if (menuItem) {
      initFormDataFromMenuItem(menuItem);
    } else {
      resetFormData();
    }
  }, [menuItem, isOpen, section]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const getOptionSelectItems = (): SelectedOption[] => {
    if (!menuItemOptionsData?.results) return [];
    return menuItemOptionsData.results.map((option: MenuItemOption) => ({
      value: option.id,
      label: option.title,
      isTemplate: option.isTemplate,
      dependsOnOption: option.dependsOnOption,
      menuItemOptionValues: option.menuItemOptionValues,
      raw: option,
    }));
  };

  const handleOptionChange = (newValue: MultiValue<SelectedOption>): void => {
    const typedNewValue = newValue as SelectedOption[];
    setSelectedOptions(typedNewValue);
    updatePriceModifiersForSelectedOptions(typedNewValue);

    const newSettings = { ...optionSettings };
    typedNewValue.forEach((option) => {
      if (!newSettings[option.value]) {
        newSettings[option.value] = {
          isRequired: false,
          maxSelections: getDefaultMaxSelections(
            option.raw?.type,
            option.raw?.menuItemOptionValues?.length
          ),
        };
      }
    });
    Object.keys(newSettings).forEach((optionId) => {
      if (!typedNewValue.some((option) => option.value === optionId)) {
        delete newSettings[optionId];
      }
    });
    setOptionSettings(newSettings);
  };

  const updatePriceModifiersForSelectedOptions = (
    options: SelectedOption[]
  ): void => {
    const updatedModifiers: PriceModifiersMap = { ...optionPriceModifiers };
    options.forEach((option: SelectedOption) => {
      if (
        !updatedModifiers[option.value] &&
        option.menuItemOptionValues &&
        !option.isTemplate &&
        !option.dependsOnOption
      ) {
        const valuePrices: { [valueId: string]: number } = {};
        option.menuItemOptionValues.forEach((value) => {
          valuePrices[value.id] = 0;
        });
        updatedModifiers[option.value] = valuePrices;
      }
    });
    Object.keys(updatedModifiers).forEach((optionId) => {
      if (!options.some((option) => option.value === optionId)) {
        delete updatedModifiers[optionId];
      }
    });
    setOptionPriceModifiers(updatedModifiers);
  };

  const handlePriceModifierChange = (
    optionId: string,
    valueId: string,
    price: number
  ): void => {
    setOptionPriceModifiers((prev) => ({
      ...prev,
      [optionId]: { ...prev[optionId], [valueId]: price },
    }));
  };

  const prepareItemOption = (option: SelectedOption): ItemOptionWithPrices => {
    const optionData: ItemOptionWithPrices = {
      menuItemOption: `/api/menu_item_options/${option.value}`,
    };

    if (isEditing && menuItem?.itemOptions) {
      const existingOption = menuItem.itemOptions.find(
        (io) => io.menuItemOption.id === option.value
      );
      if (existingOption) {
        optionData["@id"] = existingOption["@id"];
      }
    }

    const settings = optionSettings[option.value];
    if (settings) {
      optionData.isRequired = settings.isRequired;
      optionData.maxSelections = settings.maxSelections;
    }

    if (
      !option.isTemplate &&
      !option.dependsOnOption &&
      optionPriceModifiers[option.value]
    ) {
      optionData.itemOptionPrices = preparePriceModifiers(option);
    }

    return optionData;
  };

  const preparePriceModifiers = (option: SelectedOption): PriceModifier[] => {
    const prices: PriceModifier[] = [];
    Object.entries(optionPriceModifiers[option.value]).forEach(
      ([valueId, price]) => {
        const priceModifier: PriceModifier = {
          priceModifier: optionPriceToCent(price),
          menuItemOptionValue: `/api/menu_item_option_values/${valueId}`,
        };
        if (isEditing && menuItem?.itemOptions) {
          const existingOption = menuItem.itemOptions.find(
            (io) => io.menuItemOption.id === option.value
          );
          // Same row as the prefill (initOptionsAndPriceModifiers).
          const existingPrice = pickPlacementPriceRows(
            existingOption?.itemOptionPrices,
            menuItem.id
          )[valueId];
          if (existingPrice) {
            priceModifier["@id"] = existingPrice["@id"];
          }
        }
        prices.push(priceModifier);
      }
    );
    return prices;
  };

  const prepareMenuItemData = async (): Promise<MenuItemFormData> => {
    let imagePath = menuItem?.image
      ? `/api/media_objects/${menuItem.image.id}`
      : "";

    if (imageFile) {
      const uploadedMedia = await uploadMedia(imageFile);
      if (uploadedMedia) {
        imagePath = `/api/media_objects/${uploadedMedia.id}`;
      }
    }

    const itemOptions = selectedOptions.map(prepareItemOption);

    const menuItemData: MenuItemFormData = {
      title,
      description,
      isAvailable,
      basePrice: convertPriceToCent(basePrice),
      sortOrder,
      menuSection: section?.id
        ? `/api/menu_sections/${section?.id}`
        : menuItem?.menuSection,
      ...(imagePath && { image: imagePath }),
      itemOptions,
    };

    if (isEditing && menuItem) {
      menuItemData["@id"] = `/api/menu_items/${menuItem.id}`;
    }

    return menuItemData;
  };

  const validateForm = (): boolean => {
    if (!title.trim()) {
      toast.error("Le titre de l'élément est requis");
      return false;
    }
    if (!section?.id && !menuItem?.menuSection) {
      toast.error("Section invalide");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const menuItemData = await prepareMenuItemData();

      // Identity (name/description/image) lives on the dish, not the placement.
      // Editing a placement that already has a dish updates the Product so the
      // change shows on every menu the dish is on — the placement keeps only
      // its per-menu settings. New dishes still get their Product from the
      // create path (MenuItemProductListener), so this only runs on edit.
      if (isEditing && menuItem?.product?.id) {
        await updateProduct({
          id: menuItem.product.id,
          data: {
            title,
            description,
            ...(menuItemData.image && { image: menuItemData.image }),
          },
        }).unwrap();
      }

      await saveMenuItem({
        id: menuItem?.id,
        data: menuItemData,
      }).unwrap();
      toast.success(
        `Élément ${isEditing ? "mis à jour" : "ajouté"} avec succès`
      );
      onSuccess?.();
      onClose();
    } catch (error: any) {
      logger.error("MenuItemFormDrawer", "Error saving menu item:", error);
      toastApiError(error);
    }
  };

  const needsPriceModifiers = (option: SelectedOption): boolean => {
    return !option.isTemplate && !option.dependsOnOption;
  };

  const renderEditablePriceModifiers = (option: SelectedOption) => {
    if (!option.menuItemOptionValues) return null;
    return (
      <div className="pl-3 mt-2 space-y-2 border-l-2 border-border">
        <p className="font-medium text-xs text-muted-foreground">
          Modificateurs de prix pour {option.label}
        </p>
        <div className="space-y-2">
          {option.menuItemOptionValues.map((value) => (
            <div key={value.id} className="flex items-center gap-2">
              <span className="text-sm flex-1 truncate">
                {value.title}
              </span>
              <div className="w-24">
                <Input
                  type="number"
                  placeholder="0"
                  value={optionPriceModifiers[option.value]?.[value.id] || 0}
                  onChange={(e) =>
                    handlePriceModifierChange(
                      option.value,
                      value.id,
                      parseFloat(e.target.value) || 0
                    )
                  }
                  variant="secondary"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDependentPriceModifiers = (option: SelectedOption) => {
    if (!option.menuItemOptionValues || !option.dependsOnOption) return null;
    return (
      <div className="space-y-3">
        {option.menuItemOptionValues.map((value) => {
          const hasDependentPrices =
            value.valueDependentPrices && value.valueDependentPrices.length > 0;
          if (hasDependentPrices) {
            return (
              <div key={value.id} className="border-t border-border pt-2">
                <p className="text-sm font-medium">
                  {value.title}
                </p>
                <div className="space-y-2 mt-1">
                  {value.valueDependentPrices?.map(
                    (depPrice: ValueDependentPrice) => (
                      <div
                        key={depPrice.id}
                        className="flex items-center gap-2"
                      >
                        <span className="text-xs flex-1 truncate">
                          Pour {depPrice.dependsOnValue.title}
                        </span>
                        <div className="w-24">
                          <Input
                            type="number"
                            value={convertPriceFromCent(depPrice.priceModifier)}
                            disabled
                            variant="secondary"
                            className="bg-muted"
                          />
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            );
          }
          return (
            <div key={value.id} className="flex items-center gap-2">
              <span className="text-sm flex-1 truncate">
                {value.title}
              </span>
              <div className="w-24">
                <Input
                  type="number"
                  value={
                    value.basePrice ? convertPriceFromCent(value.basePrice) : 0
                  }
                  disabled
                  variant="secondary"
                  className="bg-muted"
                />
              </div>
            </div>
          );
        })}
        <p className="text-xs text-muted-foreground italic">
          Option dépendante de "{option.dependsOnOption?.title}" — les prix
          varient selon le choix parent.
        </p>
      </div>
    );
  };

  const renderTemplatePriceModifiers = (option: SelectedOption) => {
    if (!option.menuItemOptionValues) return null;
    return (
      <div className="space-y-2">
        {option.menuItemOptionValues.map((value) => {
          const base = value.basePrice
            ? convertPriceFromCent(value.basePrice)
            : 0;
          return (
            <div key={value.id} className="flex items-center gap-2">
              <span className="text-sm flex-1 truncate">
                {value.title}
              </span>
              <div className="w-24">
                <Input
                  type="number"
                  value={base}
                  disabled
                  variant="secondary"
                  className="bg-muted"
                />
              </div>
            </div>
          );
        })}
        {option.isTemplate && (
          <p className="text-xs text-muted-foreground italic">
            Option modèle — les prix sont fixés globalement.
          </p>
        )}
      </div>
    );
  };

  const renderPriceModifiers = (option: SelectedOption) => {
    if (!option.menuItemOptionValues) return null;
    if (needsPriceModifiers(option)) {
      return renderEditablePriceModifiers(option);
    }
    return (
      <div className="pl-3 mt-2 space-y-2 border-l-2 border-border">
        <p className="font-medium text-xs text-muted-foreground">
          Prix pré-définis pour {option.label} (non modifiables)
        </p>
        {option.dependsOnOption
          ? renderDependentPriceModifiers(option)
          : renderTemplatePriceModifiers(option)}
      </div>
    );
  };

  const handleIsRequiredChange = (optionId: string, isRequired: boolean) => {
    setOptionSettings((prev) => ({
      ...prev,
      [optionId]: { ...prev[optionId], isRequired },
    }));
  };

  const handleMaxSelectionsChange = (
    optionId: string,
    maxSelections: number
  ) => {
    setOptionSettings((prev) => ({
      ...prev,
      [optionId]: { ...prev[optionId], maxSelections },
    }));
  };

  return (
    <Sheet open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0"
      >
        <form
          onSubmit={handleSubmit}
          className="flex flex-col h-full min-h-0"
        >
          <header className="flex-shrink-0 px-6 py-4 border-b border-border bg-card">
            <SheetTitle className="text-base">
              {isEditing ? "Modifier l'élément" : "Ajouter un élément"}
              {section && ` - ${section.title}`}
            </SheetTitle>
            <SheetDescription hidden>
              Formulaire de création ou modification d'un élément de menu.
            </SheetDescription>
          </header>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
            <Section_ title="Informations">
              <Field>
                <Label label="Image" htmlFor="menuitem-image" />
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Aperçu"
                    className="w-20 h-20 object-cover rounded mt-1 mb-2"
                  />
                )}
                <input
                  type="file"
                  id="menuitem-image"
                  className="block w-full text-sm border bg-white rounded-lg py-2 px-2 mt-1"
                  onChange={handleImageChange}
                  accept="image/*"
                />
              </Field>
              <Field>
                <Input
                  label="Nom"
                  type="text"
                  id="menuitem-title"
                  placeholder="Nom de l'élément"
                  required
                  className="h-10 py-2 text-sm"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>
              <Field>
                <TextArea
                  label="Description"
                  placeholder="Description de l'élément"
                  id="menuitem-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <Input
                    label="Prix sur ce menu"
                    type="number"
                    id="menuitem-basePrice"
                    placeholder="Prix"
                    className="h-10 py-2 text-sm"
                    value={basePrice}
                    onChange={(e) =>
                      setBasePrice(parseFloat(e.target.value) || 0)
                    }
                    min={0}
                  />
                  {hasMenuSpecificPrice && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Ce prix s'applique uniquement à ce menu. Le prix de base
                      du plat se modifie depuis « Plats ».
                    </p>
                  )}
                </Field>
                <Field>
                  <Input
                    label="Position"
                    type="number"
                    id="menuitem-sortOrder"
                    placeholder="Position"
                    className="h-10 py-2 text-sm"
                    value={sortOrder}
                    onChange={(e) =>
                      setSortOrder(parseInt(e.target.value) || 1)
                    }
                    min={1}
                  />
                </Field>
              </div>
              <div className="flex items-start gap-2.5">
                <Checkbox
                  id="menuitem-isAvailable"
                  className="mt-0.5"
                  checked={isAvailable}
                  onCheckedChange={() => setIsAvailable(!isAvailable)}
                />
                <Label
                  htmlFor="menuitem-isAvailable"
                  label="Cet élément est disponible"
                  id="menuitem-isAvailable"
                  className="cursor-pointer"
                />
              </div>
            </Section_>

            <Section_ title="Options">
              <Field>
                <label
                  htmlFor="options"
                  className="text-sm text-card-foreground block mb-1"
                >
                  Options
                </label>
                <Select
                  inputId="options"
                  isMulti
                  name="options"
                  options={getOptionSelectItems()}
                  className="basic-multi-select text-sm"
                  classNamePrefix="select"
                  value={selectedOptions}
                  onChange={handleOptionChange}
                  placeholder="Sélectionner les options"
                />
              </Field>

              {selectedOptions.length > 0 && (
                <div className="space-y-3 text-sm">
                  {selectedOptions.map((option) => (
                    <div
                      key={option.value}
                      className="border border-border rounded-md p-3 bg-muted/30 space-y-3"
                    >
                      <h5 className="font-medium text-sm">
                        {option.label}
                      </h5>
                      <div className="grid grid-cols-1 gap-3">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`required-${option.value}`}
                            checked={
                              optionSettings[option.value]?.isRequired || false
                            }
                            onCheckedChange={(checked) =>
                              handleIsRequiredChange(
                                option.value,
                                checked as boolean
                              )
                            }
                          />
                          <Label
                            htmlFor={`required-${option.value}`}
                            label="Option requise"
                            className="cursor-pointer"
                          />
                        </div>
                        <Field>
                          <Input
                            label="Sélections max."
                            type="number"
                            min={getMinSelections(option.raw?.type)}
                            max={getMaxSelections(
                              option.raw?.type,
                              option.raw?.menuItemOptionValues?.length
                            )}
                            className="h-10 py-2 text-sm"
                            value={
                              optionSettings[option.value]?.maxSelections ||
                              getDefaultMaxSelections(
                                option.raw?.type,
                                option.raw?.menuItemOptionValues?.length
                              )
                            }
                            onChange={(e) =>
                              handleMaxSelectionsChange(
                                option.value,
                                parseInt(e.target.value) || 1
                              )
                            }
                          />
                        </Field>
                      </div>
                      {renderPriceModifiers(option)}
                    </div>
                  ))}
                </div>
              )}
            </Section_>
          </div>

          <footer className="flex-shrink-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button type="submit" isLoading={isLoading}>
              {isEditing ? "Enregistrer" : "Ajouter"}
            </Button>
          </footer>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default MenuItemFormDrawer;
